/**
 * TiptapTopToolbar — persistent floating toolbar above the paper.
 *
 * Phase 6 free-flow editor: gives the user always-visible Word-style
 * controls (B / I / U + lists + AI rewrite) regardless of selection.
 * The bubble menu still appears on selection for context-local
 * actions; this top toolbar covers the case where the user wants
 * to apply a mark before typing or click a control without
 * highlighting first.
 *
 * Position: top-center, sticky to the editor's container so it
 * stays visible as the user scrolls through long documents. Pine
 * fill on the bar; cloud-on-pine icons match the zoom toolbar
 * styling for visual consistency.
 */
import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { useMutation } from '@tanstack/react-query';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Sparkles,
  X,
} from 'lucide-react';
import { api } from '../../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../../lib/noAutofill.ts';
import { classifyAiError } from '../../../lib/aiErrorMessage.ts';
import { useAiProgress } from '../../../lib/aiProgressMessage.ts';

interface Props {
  editor: Editor | null;
  meetingId: string;
}

const PRESETS: Array<{ label: string; instruction: string }> = [
  {
    label: 'Shorter',
    instruction: 'Make it shorter - aim for roughly half the original length.',
  },
  {
    label: 'More formal',
    instruction: 'Rewrite in a more formal, professional tone.',
  },
  {
    label: 'Expand',
    instruction:
      'Expand with more detail and context, while staying true to the facts.',
  },
  {
    label: 'Simpler',
    instruction: 'Simplify the language. Short sentences, everyday words.',
  },
];

export function TiptapTopToolbar({ editor, meetingId }: Props) {
  // Force re-render on selection / transaction so active-state
  // indicators (e.g. "is bold active here?") stay fresh.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const handler = () => forceTick((n) => n + 1);
    editor.on('selectionUpdate', handler);
    editor.on('transaction', handler);
    return () => {
      editor.off('selectionUpdate', handler);
      editor.off('transaction', handler);
    };
  }, [editor]);

  const [rewriteOpen, setRewriteOpen] = useState(false);
  if (!editor) return null;

  // Floats above the paper (centered). Not sticky — ScaledPaper's
  // zoom toolbar already sticks at the top of the scrolling
  // container, and competing sticky elements would overlap. The
  // bubble menu still appears on selection for context-local
  // actions while the user has scrolled past this bar.
  return (
    // Mockup .tiptap-toolbar: floating pine pill, mx-auto fit-content,
    // padding-1 + gap-1, soft shadow. Sits centered above the paper
    // when the document is editable.
    <div className="mx-auto mb-3 flex w-fit items-center gap-1 rounded-full bg-pine p-1 shadow-[0_4px_14px_rgba(43,42,47,0.12)]">
      <ToolbarButton
        title="Bold (Cmd+B)"
        icon={<Bold size={13} strokeWidth={2.5} />}
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        title="Italic (Cmd+I)"
        icon={<Italic size={13} strokeWidth={2.5} />}
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        title="Underline (Cmd+U)"
        icon={<UnderlineIcon size={13} strokeWidth={2.5} />}
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />

      <Divider />

      <ToolbarButton
        title="Bullet list"
        icon={<List size={13} strokeWidth={2.5} />}
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        title="Numbered list"
        icon={<ListOrdered size={13} strokeWidth={2.5} />}
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />

      <Divider />

      <RewriteButton
        editor={editor}
        meetingId={meetingId}
        open={rewriteOpen}
        setOpen={setRewriteOpen}
      />
    </div>
  );
}

function ToolbarButton({
  title,
  icon,
  active,
  onClick,
}: {
  title: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onClick={onClick}
      // Mockup .tt-btn: 26x26 transparent + cream text. Active state
      // (.tt-btn--active) inverts to cream bg + pine text so the
      // user always knows which formatting is on for the cursor.
      className={
        'flex h-[26px] w-[26px] items-center justify-center rounded-full text-[12px] font-bold transition-colors ' +
        (active
          ? 'bg-cream text-pine'
          : 'text-cream hover:bg-cream/15')
      }
    >
      {icon}
    </button>
  );
}

function Divider() {
  // Mockup .tt-divider: 1px x 14px cream/20 hairline.
  return <div aria-hidden className="mx-0.5 h-3.5 w-px bg-cream/20" />;
}

function RewriteButton({
  editor,
  meetingId,
  open,
  setOpen,
}: {
  editor: Editor;
  meetingId: string;
  open: boolean;
  setOpen: (v: boolean) => void;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState<
    | { kind: 'presets' }
    | { kind: 'custom'; instruction: string }
    | { kind: 'pending' }
    | { kind: 'result'; rewrittenText: string; rationale?: string }
  >({ kind: 'presets' });
  const rangeRef = useRef<{ from: number; to: number } | null>(null);

  // Close popover on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setStage({ kind: 'presets' });
        rangeRef.current = null;
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, setOpen]);

  const rewriteMutation = useMutation({
    mutationFn: async ({
      selectedText,
      instruction,
    }: {
      selectedText: string;
      instruction: string;
    }) =>
      api.post<{ rewrittenText: string; rationale?: string; usage: unknown }>(
        `/meetings/${meetingId}/rewrite`,
        { selectedText, instruction },
      ),
    onSuccess: (data) => {
      setStage({
        kind: 'result',
        rewrittenText: data.rewrittenText,
        rationale: data.rationale,
      });
    },
    onError: () => {
      setStage({ kind: 'presets' });
    },
  });

  // Track whether the user currently has a non-empty selection.
  // Used to disable the trigger + gray out the presets so the
  // user doesn't click a preset and THEN find out they needed
  // to select first (the previous version popped a late alert).
  const { from: selFrom, to: selTo } = editor.state.selection;
  const hasSelection =
    selFrom !== selTo &&
    editor.state.doc.textBetween(selFrom, selTo, '\n').trim().length > 0;

  const startRewrite = (instruction: string) => {
    if (!hasSelection) return;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, '\n');
    if (!selectedText.trim()) return;
    rangeRef.current = { from, to };
    setStage({ kind: 'pending' });
    rewriteMutation.mutate({ selectedText, instruction });
  };

  const applyRewrite = (text: string) => {
    if (!rangeRef.current) return;
    const { from, to } = rangeRef.current;
    editor.chain().focus().insertContentAt({ from, to }, text).run();
    rangeRef.current = null;
    setStage({ kind: 'presets' });
    rewriteMutation.reset();
    setOpen(false);
  };

  // Friendly error classification + rotating progress messages.
  const classifiedError = rewriteMutation.error
    ? classifyAiError(rewriteMutation.error)
    : null;
  const progress = useAiProgress(stage.kind === 'pending');

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        disabled={!hasSelection}
        onClick={() => {
          if (open) {
            setOpen(false);
            setStage({ kind: 'presets' });
          } else {
            setOpen(true);
          }
        }}
        title={
          hasSelection
            ? 'Rewrite with AI'
            : 'Select some text in the document first to rewrite.'
        }
        aria-label="Rewrite with AI"
        // Mockup .tt-rewrite: cream text + transparent + rounded
        // pill + padding 4/10 + 11px medium with Sparkles icon.
        // Active (popover open) inverts to cream bg + pine text.
        className={
          'flex h-[26px] items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium transition-colors ' +
          (open
            ? 'bg-cream text-pine'
            : hasSelection
              ? 'text-cream hover:bg-cream/15'
              : 'cursor-not-allowed text-cream/40')
        }
      >
        <Sparkles size={12} strokeWidth={2.5} />
        Rewrite
      </button>

      {open && (
        // Popover panel — bone surface so it visually peers with the
        // editor's left-pane panels (same warm-neutral family).
        // Bigger shadow than the rest of the editor chrome since the
        // popover floats above the document.
        <div
          className="absolute right-0 top-full z-30 mt-1.5 w-80 overflow-hidden rounded-xl border border-charcoal/12 bg-bone shadow-[0_12px_32px_rgba(43,42,47,0.16)]"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {stage.kind === 'presets' && (
            <div className="flex flex-col p-2 text-[12px] text-charcoal">
              <div className="mb-1.5 px-2 pt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-pine">
                Rewrite selection
              </div>
              {!hasSelection && (
                <div className="mb-1 px-2 py-1 text-[11px] italic text-charcoal/65">
                  Select some text in the document first.
                </div>
              )}
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  disabled={!hasSelection}
                  onClick={() => startRewrite(p.instruction)}
                  className={
                    'rounded-lg px-2.5 py-1.5 text-left transition-colors ' +
                    (hasSelection
                      ? 'hover:bg-pine/[0.08] hover:text-pine'
                      : 'cursor-not-allowed text-charcoal/40')
                  }
                >
                  {p.label}
                </button>
              ))}
              <div className="my-1.5 border-t border-charcoal/10" />
              <button
                type="button"
                disabled={!hasSelection}
                onClick={() =>
                  setStage({ kind: 'custom', instruction: '' })
                }
                className={
                  'rounded-lg px-2.5 py-1.5 text-left transition-colors ' +
                  (hasSelection
                    ? 'hover:bg-pine/[0.08] hover:text-pine'
                    : 'cursor-not-allowed text-charcoal/40')
                }
              >
                Custom instruction…
              </button>
            </div>
          )}

          {stage.kind === 'custom' && (
            <form
              className="p-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (stage.instruction.trim()) {
                  startRewrite(stage.instruction.trim());
                }
              }}
            >
              <input
                {...NO_AUTOFILL}
                type="text"
                autoFocus
                value={stage.instruction}
                onChange={(e) =>
                  setStage({ kind: 'custom', instruction: e.target.value })
                }
                placeholder="How should Claude rewrite this?"
                // Same input chrome as the left-pane panels: cream
                // bg + charcoal/10 + rounded-[8px] + pine focus ring.
                className="w-full rounded-[8px] border border-charcoal/10 bg-cream px-2.5 py-1.5 text-[12px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
              />
              <div className="mt-2.5 flex items-center justify-between text-[11px]">
                <button
                  type="button"
                  onClick={() => setStage({ kind: 'presets' })}
                  className="text-charcoal/65 transition-colors hover:text-pine"
                >
                  ← back
                </button>
                <button
                  type="submit"
                  disabled={!stage.instruction.trim()}
                  className="rounded-full bg-pine px-3.5 py-1 text-[11px] font-medium text-cream transition-colors hover:bg-pine-deep disabled:opacity-50"
                >
                  Rewrite
                </button>
              </div>
            </form>
          )}

          {stage.kind === 'pending' && (
            <div className="flex items-center gap-2 p-3.5 text-[12px] text-charcoal">
              <Sparkles
                size={12}
                className="shrink-0 animate-pulse text-pine"
              />
              <span className="flex-1">{progress.message}</span>
              {progress.elapsedSeconds >= 5 && (
                <span className="text-[10px] tabular-nums text-charcoal/50">
                  {progress.elapsedSeconds}s
                </span>
              )}
            </div>
          )}

          {stage.kind === 'result' && (
            <div className="p-3.5 text-[12px]">
              <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-pine">
                Claude's rewrite
              </div>
              {/* Result body: pine-soft + pine/30 (matches the
                  .ai-result chrome family used by the AI assist
                  inline result panels in the left pane). */}
              <div className="mb-2 rounded-[8px] border border-pine/30 bg-pine/[0.08] px-3 py-2 text-charcoal">
                {stage.rewrittenText}
              </div>
              {stage.rationale && (
                <div className="mb-2 text-[10px] italic text-charcoal/65">
                  {stage.rationale}
                </div>
              )}
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setStage({ kind: 'presets' });
                    rewriteMutation.reset();
                    rangeRef.current = null;
                  }}
                  className="text-[11px] text-charcoal/65 transition-colors hover:text-pine"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={() => applyRewrite(stage.rewrittenText)}
                  className="rounded-full bg-pine px-3.5 py-1 text-[11px] font-medium text-cream transition-colors hover:bg-pine-deep"
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          {classifiedError && (
            <div
              // Error footer: amber for rate-limit (recoverable in
              // a few hours), terra-cotta for everything else (matches
              // the rest of the app's error chrome). Top-bordered so
              // it reads as appended to whichever stage is active.
              className={
                'border-t px-3.5 py-2 text-[11px] ' +
                (classifiedError.kind === 'rateLimit'
                  ? 'border-amber-300/60 bg-amber-50 text-amber-900'
                  : 'border-terra-cotta/30 bg-terra-cotta/[0.08] text-terra-cotta')
              }
            >
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <div className="font-semibold">{classifiedError.title}</div>
                  {classifiedError.advice && (
                    <div className="mt-0.5 opacity-80">
                      {classifiedError.advice}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => rewriteMutation.reset()}
                  className="shrink-0 opacity-60 transition-opacity hover:opacity-100"
                  aria-label="Dismiss error"
                >
                  <X size={10} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
